<!--
  --
  -- 日時入力コンポーネント
  --
  -->
<template>
  <c-input
    :label="label"
    :value="value"
    :rules="rules"
  >
    <div class="d-flex mt-2">

      <!-- 日付入力 -->
      <v-dialog
        v-model="dateDialog"
        width="304"
        :disabled="dateChange"
      >
        <template #activator="{on}">
          <v-btn text outlined class="body-2 mr-4" v-on="on">
            <span>{{date | timeformat('Y年m月d日 (x)')}}</span>
          </v-btn>
        </template>

        <c-date-picker-card
          v-model="date"
          :allowed-dates="allowedDates"
          @change="onChangeDate"
          @close="dateDialog = false"
        />
      </v-dialog>

      <!-- 時刻入力 -->
      <v-dialog
        v-model="timeDialog"
        width="304"
      >
        <template #activator="{on}">
          <v-btn text outlined class="body-2" v-on="on">
            <span>{{time}}</span>
          </v-btn>
        </template>

        <c-time-picker-card
          v-if="timeDialog"
          v-model="time"
          :min="openTime.begin"
          :max="openTime.end"
          :allowed-times="allowedTimes"
          @close="timeDialog = false"
        />
      </v-dialog>
    </div>
  </c-input>
</template>

<script>
import CInput from '@/components/CInput'
import CDatePickerCard from './CDatePickerCard'
import CTimePickerCard from './CTimePickerCard'

import * as time from '@/libs/time'

export default {
  name: 'CTimeSelect',
  components: {
    CInput,
    CDatePickerCard,
    CTimePickerCard
  },

  model: {
    prop: 'value',
    event: 'change'
  },

  props: {
    /**
     * ラベル
     */
    label: {
      type: String,
      default: ''
    },

    /**
     * 日時の値
     */
    value: {
      type: String,
      required: true
    },

    /**
     * カレンダー
     */
    dates: {
      type: Object,
      required: true
    },

    /**
     * 入力可能範囲
     */
    allow: {
      type: Object,
      required: true
    },

    // 入力チェックルール
    rules: {
      type: Array,
      default: () => []
    },

    // 日付の修正可否
    dateDisabled: {
      type: Boolean,
      required: true
    }
  },

  data: () => ({
    // 入力された日付
    date: '',

    // 入力された時刻
    time: '',

    dateDialog: false,
    timeDialog: false
  }),

  computed: {
    /**
     * 日付の入力可否
     */
    dateChange () {
      return this.dateDisabled
    },

    /**
     * 日付の入力範囲を返す
     */
    dateRange () {
      return {
        begin: time.format('Y-m-d', this.allow.begin),
        end: time.format('Y-m-d', this.allow.end)
      }
    },

    /**
     * 選択された日付情報
     */
    dateValue () {
      return this.date ? this.dates[this.date] : { opened: false }
    },

    /**
     * 選択された日付の営業時間を返す
     */
    openTime () {
      const d = this.dateValue
      return (d.opened)
        ? { begin: d.begin, end: d.end }
        : { begin: '00:00', end: '23:30' }
    },

    /**
     * 選択された日付の選択可能な時間を返す
     */
    limitedTime () {
      const d = { ...this.dates[this.date] }
      if (!d.opened) {
        return null
      }

      if (this.date === time.format('Y-m-d', this.allow.begin)) {
        d.begin = time.format('H:i', this.allow.begin)
      }
      if (this.date === time.format('Y-m-d', this.allow.end)) {
        d.end = time.format('H:i', this.allow.end)
      }

      return d
    },

    /**
     * 入力された結果の日時
     */
    datetime () {
      return `${this.date} ${this.time}`
    }
  },

  watch: {
    value: {
      immediate: true,
      handler (newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          const tm = time.create(newValue)
          this.date = time.format('Y-m-d', tm)
          this.time = time.format('H:i', tm)
        }
      }
    },

    datetime (value) {
      this.$emit('change', value)
    }
  },

  methods: {
    /**
     * c-date-picker-card からの問い合わせで、日付が入力可能かを返す
     */
    allowedDates (value) {
      // dateRange の範囲外なら false
      if (value < this.dateRange.begin || this.dateRange.end < value) {
        return false
      }

      // 営業していれば true
      const d = this.dates[value]
      return d && d.opened
    },

    /**
     * c-time-picker-card からの問い合わせで、時刻が入力可能かを返す
     */
    allowedTimes (value) {
      // 選択された日付の選択可能な時間を取得する
      const d = this.limitedTime

      // 営業していなければ、すべて false
      if (!d) {
        return false
      }

      // 選択可能な時間の範囲なら true
      return d.begin <= value && value <= d.end
    },

    /**
     * 日付が変更された
     */
    onChangeDate (value) {
      // 日付が変更された結果、選択可能時間外になった場合に時刻を調整する
      const limitedTime = this.limitedTime

      if (this.time < limitedTime.begin) {
        this.time = limitedTime.begin
      }
      if (limitedTime.end < this.time) {
        this.time = limitedTime.end
      }
    }
  }
}
</script>
